// src/main.ts
// THE SHELL: a hash router over six screens, the queue started once, and the service worker.
//
// A hash router (`#/coach`) rather than the History API, deliberately: this app ships as STATIC
// FILES, and `/coach` as a real path 404s on any host that is not configured to rewrite everything
// to index.html. A hash needs no server configuration at all, which is the point of a static build.

import './styles.css';
import { el, mount } from './ui/dom';
import { apiBaseLabel } from './config';
import { canRefresh, loadSession, onAuthChange, startTokenRenewal } from './auth/session';
import { consumeJoinFragment, signInScreen } from './ui/screens/signin';
import { coachScreen } from './ui/screens/coach';
import { captureScreen } from './ui/screens/capture';
import { queueScreen } from './ui/screens/queue';
import { passportScreen } from './ui/screens/passport';
import { parentScreen } from './ui/screens/parent';
import { academyScreen } from './ui/screens/academy';
import { deliveriesScreen } from './ui/screens/deliveries';
import { focusScreen } from './ui/screens/focus';
import { reviewsScreen } from './ui/screens/reviews';
import { standardTestsScreen } from './ui/screens/standard-tests';
import { progressScreen } from './ui/screens/progress';
import { countJobs, listJobs, startQueue, subscribe } from './queue/queue';
import { getState, onStateChange } from './ui/state';

type Route =
  | 'signin' | 'coach' | 'academy' | 'capture' | 'deliveries' | 'focus'
  | 'queue' | 'reviews' | 'standard-tests' | 'progress' | 'passport' | 'parent';

// Ordered by the shape of a real session, not alphabetically: set the day up, run the
// net, deal with what the net produced, then the read-only surfaces. `queue` sits next to
// the two screens that fill it. A coach at a ground moves left to right.
const ROUTES: ReadonlyArray<{ id: Route; label: string }> = [
  { id: 'coach', label: 'Coach' },
  { id: 'academy', label: 'Academy' },
  { id: 'capture', label: 'Capture' },
  { id: 'deliveries', label: 'Balls' },
  { id: 'focus', label: 'Focus' },
  { id: 'queue', label: 'Queue' },
  { id: 'reviews', label: 'Review' },
  { id: 'standard-tests', label: 'Tests' },
  { id: 'progress', label: 'Progress' },
  { id: 'passport', label: 'Passport' },
  { id: 'parent', label: 'Parent' },
  { id: 'signin', label: 'Account' },
];

function currentRoute(): Route {
  const h = location.hash.replace(/^#\/?/, '') as Route;
  return ROUTES.some((r) => r.id === h) ? h : 'coach';
}

const app = document.getElementById('app') as HTMLElement;
const nav = document.getElementById('nav') as HTMLElement;
const banner = document.getElementById('banner') as HTMLElement;

// ── WHY THIS IS NOT JUST `if (rendering) return` ANY MORE (26 Aug 2026) ────────────────────────
//
// The founder reported: tapping a roster row does nothing, and every tap that DOES work throws the
// page back to the top of Coach and takes seconds to come back. Both came from this function.
//
// (1) THE DROPPED TAP. `if (rendering) return` DISCARDED the request. Every screen here awaits
//     network reads, so `rendering` is true for as long as those take — and a tap during that
//     window ran `setAthlete(...)` and then had its redraw thrown away. State changed; the screen
//     did not. "Nobody selected. Tap a roster row above." stayed on screen after a tap that had in
//     fact registered. A dropped render is indistinguishable from a tap that never landed, which is
//     the same class of defect as everything else found today: the failure looks like the success.
//     Now a request that arrives mid-render is COALESCED — remembered and served when this pass
//     ends — so the last tap always wins and none is silently lost.
//
// (2) THE JUMP BACK TO THE TOP. `mount(app, screen)` ran BEFORE the awaits. The screen was emptied
//     immediately, scroll collapsed to the top because there was no longer any content, and the
//     page refilled a second or two later as each fetch returned. That reads exactly as "it went to
//     the main page and came back" — because visually it did. The screen is now built OFF-DOM and
//     swapped in only once it is ready; the old screen stays up, and scroll is preserved when the
//     route has not changed. No screen touches layout or focus while building, so building detached
//     is safe (checked before making this change).
let rendering = false;
let renderPending = false;
let mountedRoute: Route | null = null;

async function render(): Promise<void> {
  if (rendering) { renderPending = true; return; }   // COALESCE — never discard
  rendering = true;
  try {
    do {
      renderPending = false;
      await renderOnce();
    } while (renderPending);                          // a tap that arrived mid-pass is served here
  } finally {
    rendering = false;
  }
}

async function renderOnce(): Promise<void> {
  const route = currentRoute();
  const sameRoute = route === mountedRoute;
  const keepScroll = sameRoute ? window.scrollY : 0;

  renderNav(route);
  app.setAttribute('aria-busy', 'true');              // the old screen stays, dimmed, while we load

  const screen = el('div', { class: 'screen' });
  try {
    switch (route) {
      case 'signin': await signInScreen(screen, { onSignedIn: () => void render() }); break;
      case 'coach': await coachScreen(screen, () => void render()); break;
      case 'capture': await captureScreen(screen, () => void render()); break;
      case 'queue': await queueScreen(screen); break;
      case 'passport': await passportScreen(screen, () => void render()); break;
      case 'parent': await parentScreen(screen, () => void render()); break;
      case 'academy': await academyScreen(screen, () => void render()); break;
      case 'deliveries': await deliveriesScreen(screen, () => void render()); break;
      case 'focus': await focusScreen(screen, () => void render()); break;
      case 'reviews': await reviewsScreen(screen, () => void render()); break;
      case 'standard-tests': await standardTestsScreen(screen, () => void render()); break;
      case 'progress': await progressScreen(screen, () => void render()); break;
    }
  } catch (e: unknown) {
    // Building off-DOM means a throw would otherwise leave the PREVIOUS screen up with no sign that
    // anything failed — a silence the old code did not have, because it had already blanked the
    // page. Say so instead.
    mount(screen, el('p', { class: 'warn' },
      `This screen could not be built: ${String((e as Error)?.message ?? e)}. `
      + 'Nothing was sent or changed. Switching tabs and back will try again.'));
  } finally {
    app.removeAttribute('aria-busy');
  }

  mount(app, screen);                                 // swap in ONLY when it is ready
  mountedRoute = route;
  window.scrollTo(0, keepScroll);                     // same route keeps its place; a new one starts at 0
}

function renderNav(active: Route): void {
  const items = ROUTES.map((r) => el('a', {
    class: `nav-item${r.id === active ? ' nav-active' : ''}`,
    href: `#/${r.id}`,
  }, r.label));
  mount(nav, ...items);

  // A scrolling bar solves reachability but not orientation: land on Passport and the bar is still
  // showing Coach, so the highlighted tab is off-screen and you cannot tell where you are. Bring
  // the active one into view. `block: 'nearest'` so this never scrolls the PAGE, only the bar.
  const i = ROUTES.findIndex((r) => r.id === active);
  items[i]?.scrollIntoView({ inline: 'center', block: 'nearest' });
}

/**
 * The always-visible truth bar: which backend, whether there is a token, what the queue holds, and
 * whether the browser thinks it is online.
 *
 * It exists so nobody has to guess which deployment a screen is talking to. "It works on my machine"
 * about a DARK deployment is almost always this question unanswered.
 */
async function renderBanner(): Promise<void> {
  const session = await loadSession();
  const st = getState();
  const counts = countJobs(await listJobs());
  mount(banner,
    el('span', { class: 'banner-item' }, `API: ${apiBaseLabel()}`),
    // Three states, not two. "signed in" and "signed in with a token that cannot renew" behave
    // identically right up until the hour is out, and that is exactly when somebody is filming.
    // A LINK, not a span. This chip reported "not signed in" and offered no way to do anything
    // about it — the only route to sign-in was the twelfth tab in a nav bar that could not scroll,
    // so on a phone the state was visible and unreachable at the same time. Every state here is now
    // one tap from the screen that changes it, including "token stored (cannot renew)", which is
    // the one a coach hits mid-session when the hour runs out.
    el('a', {
      class: `banner-item banner-link ${session ? 'ok' : 'warn'}`,
      href: '#/signin',
    }, !session ? 'not signed in — tap to sign in'
      : canRefresh(session) ? 'signed in'
      : 'token stored (cannot renew)'),
    el('span', { class: `banner-item ${navigator.onLine ? '' : 'warn'}` },
      navigator.onLine ? 'online' : 'offline — work is queued'),
    el('span', { class: 'banner-item' },
      `queue: ${counts.queued} queued · ${counts.held} held · ${counts.failed} failed · ${counts.done} done`),
    st.athlete && el('span', { class: 'banner-item' }, `athlete: ${st.athlete.label}`),
    st.sessionId && el('span', { class: 'banner-item' }, `session: ${st.sessionId.slice(0, 8)}…`),
  );
}

async function boot(): Promise<void> {
  // A join link's token is taken BEFORE anything renders, so the first screen is already signed in.
  await consumeJoinFragment();

  startQueue();

  // Renew a token that is about to expire even while nothing is being requested — the Capture
  // screen open and untouched for fifty minutes during a net session is the case this covers.
  startTokenRenewal();

  // A session that ended is a fact the whole shell needs, not just the screen that noticed. A 401
  // on the Coach screen whose refresh was definitively rejected clears the session; without this,
  // the banner would keep saying "signed in" until the next navigation.
  onAuthChange((e) => {
    void renderBanner();
    // Re-render on a sign-out so the current screen shows its "not signed in" refusal rather than
    // stale rows read with a credential that no longer exists.
    if (e === 'signed_out') void render();
  });

  subscribe(() => void renderBanner());
  onStateChange(() => void renderBanner());
  window.addEventListener('hashchange', () => void render());
  window.addEventListener('online', () => void renderBanner());
  window.addEventListener('offline', () => void renderBanner());

  await render();
  await renderBanner();

  // The service worker makes this installable and lets the shell open with no network. It caches
  // only THIS app's own static files — never an API response. See sw.js for why that line is hard.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch((e: unknown) => {
      // Registration fails on plain http from a non-localhost origin (a LAN IP during ground
      // testing). Reported, not swallowed: the app still works, it is just not installable.
      console.warn('[pwa] service worker not registered:', e);
    });
  }
}

void boot();
